/**
 * client/src/features/income/components/AddIncome/BasicIncomeForm.jsx
 * 
 * Form component for adding non-salary income types like rental, business, etc.
 * Provides a simplified form with basic income details.
 */
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  ArrowLeftIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/incomeUtils';
import { calculateTaxSummary, formatTaxSummary } from '../../utils/taxCalculator';
import useIncome from '../../hooks/useIncome';
import BucketOwnerSelector from './BucketOwnerSelector';
import { useAuthContext } from '../../../auth/context/AuthContext';

// Frequency options for income
const FREQUENCY_OPTIONS = [
  { id: 'WEEKLY', label: 'Weekly' },
  { id: 'BI_WEEKLY', label: 'Bi-Weekly' },
  { id: 'MONTHLY', label: 'Monthly' }
];

// State options for tax calculation
const STATE_OPTIONS = [
  { id: 'AL', label: 'Alabama' },
  { id: 'AK', label: 'Alaska' },
  { id: 'AZ', label: 'Arizona' },
  { id: 'AR', label: 'Arkansas' },
  { id: 'CA', label: 'California' },
  { id: 'CO', label: 'Colorado' },
  { id: 'CT', label: 'Connecticut' },
  { id: 'DE', label: 'Delaware' },
  { id: 'FL', label: 'Florida' },
  { id: 'GA', label: 'Georgia' },
  { id: 'HI', label: 'Hawaii' },
  { id: 'ID', label: 'Idaho' },
  { id: 'IL', label: 'Illinois' },
  { id: 'IN', label: 'Indiana' },
  { id: 'IA', label: 'Iowa' },
  { id: 'KS', label: 'Kansas' },
  { id: 'KY', label: 'Kentucky' },
  { id: 'LA', label: 'Louisiana' },
  { id: 'ME', label: 'Maine' },
  { id: 'MD', label: 'Maryland' },
  { id: 'MA', label: 'Massachusetts' },
  { id: 'MI', label: 'Michigan' },
  { id: 'MN', label: 'Minnesota' },
  { id: 'MS', label: 'Mississippi' },
  { id: 'MO', label: 'Missouri' },
  { id: 'MT', label: 'Montana' },
  { id: 'NE', label: 'Nebraska' },
  { id: 'NV', label: 'Nevada' },
  { id: 'NH', label: 'New Hampshire' },
  { id: 'NJ', label: 'New Jersey' },
  { id: 'NM', label: 'New Mexico' },
  { id: 'NY', label: 'New York' },
  { id: 'NC', label: 'North Carolina' },
  { id: 'ND', label: 'North Dakota' },
  { id: 'OH', label: 'Ohio' },
  { id: 'OK', label: 'Oklahoma' },
  { id: 'OR', label: 'Oregon' },
  { id: 'PA', label: 'Pennsylvania' },
  { id: 'RI', label: 'Rhode Island' },
  { id: 'SC', label: 'South Carolina' },
  { id: 'SD', label: 'South Dakota' },
  { id: 'TN', label: 'Tennessee' },
  { id: 'TX', label: 'Texas' },
  { id: 'UT', label: 'Utah' },
  { id: 'VT', label: 'Vermont' },
  { id: 'VA', label: 'Virginia' },
  { id: 'WA', label: 'Washington' },
  { id: 'WV', label: 'West Virginia' },
  { id: 'WI', label: 'Wisconsin' },
  { id: 'WY', label: 'Wyoming' },
  { id: 'DC', label: 'District of Columbia' }
];

// Business structure options for Business income
const BUSINESS_STRUCTURES = [
  { id: 'SOLE_PROPRIETOR', label: 'Sole Proprietorship' },
  { id: 'LLC', label: 'Limited Liability Company (LLC)' },
  { id: 'S_CORP', label: 'S Corporation' },
  { id: 'C_CORP', label: 'C Corporation' },
  { id: 'PARTNERSHIP', label: 'Partnership' }
];

// Property types for Rental income
const PROPERTY_TYPES = [
  { id: 'RESIDENTIAL', label: 'Residential' },
  { id: 'COMMERCIAL', label: 'Commercial' },
  { id: 'VACATION', label: 'Vacation/Short-term' },
  { id: 'MULTI_FAMILY', label: 'Multi-family' }
];

// Payment terms for Contract income
const PAYMENT_TERMS = [
  { id: 'HOURLY', label: 'Hourly Rate' },
  { id: 'FIXED', label: 'Fixed Amount' },
  { id: 'MILESTONE', label: 'Milestone-based' },
  { id: 'RETAINER', label: 'Monthly Retainer' }
];

/**
 * Format a number as currency with commas and 2 decimal places
 * Only used when field loses focus, not during typing
 */
const formatAsCurrency = (value) => {
  if (!value) return '';
  
  // Remove all non-numeric characters except decimal point
  let number = value.replace(/[^0-9.]/g, '');
  
  // Ensure only one decimal point
  const parts = number.split('.');
  if (parts.length > 2) {
    number = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Parse to float and format with commas and 2 decimal places
  let formatted = '';
  if (number) {
    const num = parseFloat(number);
    if (!isNaN(num)) {
      // Format with commas for thousands
      formatted = num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } else {
      formatted = number; // Keep as is if not a valid number yet
    }
  }
  
  return formatted;
};

/**
 * BasicIncomeForm Component
 * 
 * Form for adding non-salary income types with dynamic fields based on selected type.
 * Also handles editing existing income.
 * 
 * @param {Object} props
 * @param {string} props.bucketId - ID of the bucket to add income to
 * @param {string} props.incomeType - Type of income (BUSINESS, RENTAL, etc)
 * @param {Object} props.income - Existing income to edit (optional)
 * @param {boolean} props.isEditing - Flag indicating if we're in edit mode
 * @param {Function} props.onSuccess - Callback for successful form submission
 * @param {Function} props.onBack - Callback to go back to the previous step
 */
const BasicIncomeForm = ({ bucketId, incomeType, income = null, isEditing, onSuccess, onBack }) => {
  const { user } = useAuthContext();
  // Determine if we're in edit mode
  isEditing = isEditing || !!income;
  
  // State for bucket and owner selection (for partner income creation)
  const [selectedBucketId, setSelectedBucketId] = useState(bucketId);
  const [selectedOwnerId, setSelectedOwnerId] = useState(
    income?.owner?._id || income?.owner || user?._id || user?.id
  );
  
  // Update selectedBucketId when bucketId prop changes
  useEffect(() => {
    if (bucketId) {
      setSelectedBucketId(bucketId);
    }
  }, [bucketId]);
  
  // Prepare default values based on edit mode
  const getDefaultValues = () => {
    if (isEditing && income) {
      return {
        name: income.name || '',
        amount: income.amount || income.grossAmount || '',
        frequency: income.frequency || '',
        state: (income.location && income.location.state) || '',
        startDate: income.startDate ? new Date(income.startDate).toISOString().split('T')[0] : '',
        endDate: income.endDate ? new Date(income.endDate).toISOString().split('T')[0] : '',
        businessName: income.businessName || (income.typeSpecificDetails?.business?.name) || '',
        businessStructure: income.businessStructure || (income.typeSpecificDetails?.business?.structure) || '',
        propertyAddress: income.propertyAddress || (income.typeSpecificDetails?.rental?.address) || '',
        propertyType: income.propertyType || (income.typeSpecificDetails?.rental?.type) || '',
        client: income.client || (income.typeSpecificDetails?.contract?.clientName) || '',
        paymentTerms: income.paymentTerms || (income.typeSpecificDetails?.contract?.terms) || '',
        notes: income.notes || ''
      };
    }
    
    return {
      name: '',
      amount: '',
      frequency: '',
      state: '',
      startDate: '',
      businessName: '',
      businessStructure: '',
      propertyAddress: '',
      propertyType: '',
      client: '',
      paymentTerms: '',
      notes: ''
    };
  };
  
  // Form state using React Hook Form
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: getDefaultValues()
  });
  
  // Additional state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [showFileUpload, setShowFileUpload] = useState(incomeType === 'CONTRACT');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState(null);
  
  // Get income management functions from custom hook
  const { createIncome, updateIncome } = useIncome();
  
  // Watch form values for tax calculation
  const watchedAmount = watch('amount');
  const watchedFrequency = watch('frequency');
  const watchedState = watch('state');
  
  // Format amount on blur
  const handleAmountBlur = (e) => {
    const value = e.target.value;
    if (value) {
      const formatted = formatAsCurrency(value);
      setValue('amount', formatted);
    }
  };
  
  // Parse for tax calculation preview
  const parseAmountForTax = (value) => {
    if (!value) return 0;
    return typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  };
  
  // Calculate tax preview based on current form values
  const previewTaxSummary = calculateTaxSummary({
    type: incomeType,
    amount: parseAmountForTax(watchedAmount),
    frequency: watchedFrequency,
    state: watchedState
  });
  
  // Format tax summary for display
  const formattedTaxSummary = formatTaxSummary(
    previewTaxSummary, 
    parseAmountForTax(watchedAmount),
    watchedFrequency
  );
  
  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadedFile(file);
    setIsProcessing(true);
    
    // Simulate processing for contract files
    setTimeout(() => {
      try {
        // Simulate data extraction (would be real in production)
        const extractedData = {
          client: 'ABC Tech Solutions',
          amount: '3500.00',
          paymentTerms: 'FIXED'
        };
        
        // Update form with extracted data
        if (incomeType === 'CONTRACT') {
          register('client').onChange({ target: { value: extractedData.client } });
          register('amount').onChange({ target: { value: extractedData.amount } });
          register('paymentTerms').onChange({ target: { value: extractedData.paymentTerms } });
        }
        
        setIsProcessing(false);
      } catch (err) {
        console.error('Error processing document:', err);
        setProcessingError('Failed to process the document. Please enter details manually.');
        setIsProcessing(false);
      }
    }, 1500);
  };
  
  // Form submission handler
  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setSubmissionError(null);
      
      // Use selected bucket and owner if available, otherwise fall back to props
      const finalBucketId = selectedBucketId || bucketId;
      const finalOwnerId = selectedOwnerId || user?._id || user?.id;
      
      // 🔍 COMPREHENSIVE DEBUG LOGGING
      console.log('📝 BasicIncomeForm - Form Submission:', {
        formData: data,
        selectedBucketId,
        selectedOwnerId,
        bucketId: bucketId,
        finalBucketId,
        finalOwnerId,
        currentUserId: user?._id || user?.id,
        incomeType,
        isEditing
      });
      
      if (!finalBucketId) {
        setSubmissionError('Please select a bucket for this income');
        setIsSubmitting(false);
        return;
      }
      
      // Format the data structure for API
      const incomeData = {
        name: data.name,
        amount: parseFloat(data.amount.toString().replace(/,/g, '')) || 0,
        frequency: data.frequency.toLowerCase(),
        startDate: data.startDate,
        endDate: data.endDate || null,
        bucketId: finalBucketId,
        bucket: finalBucketId,
        owner: finalOwnerId, // ⭐ WHO this income belongs to
        notes: data.notes,
        type: incomeType.toUpperCase(),
        category: incomeType.toUpperCase(),
        location: {
          state: data.state
        }
      };
      
      // Add type-specific details based on income type
      switch(incomeType.toUpperCase()) {
        case 'BUSINESS':
          incomeData.typeSpecificDetails = {
            business: {
              name: data.businessName,
              structure: data.businessStructure
            }
          };
          break;
        case 'RENTAL':
          incomeData.typeSpecificDetails = {
            rental: {
              address: data.propertyAddress,
              type: data.propertyType
            }
          };
          break;
        case 'CONTRACT':
          incomeData.typeSpecificDetails = {
            contract: {
              clientName: data.client,
              terms: data.paymentTerms
            }
          };
          break;
        // Add more cases for other income types as needed
      }
      
      console.log(`📝 ${isEditing ? 'Updating' : 'Creating'} ${incomeType} income:`, incomeData);
      
      // Use the appropriate function based on isEditing
      if (isEditing && income) {
        await updateIncome(bucketId, income._id, incomeData);
      } else {
        await createIncome(bucketId, incomeData);
      }
      
      // Call success callback
      onSuccess();
    } catch (error) {
      console.error(`❌ Error ${isEditing ? 'updating' : 'creating'} income:`, error);
      setSubmissionError(`Failed to ${isEditing ? 'update' : 'create'} income. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Get income type label for display
  const getIncomeTypeLabel = (type) => {
    if (!type) return '';
    
    // Make first letter uppercase and the rest lowercase
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  };
  
  // Calculate effective tax rate
  const calculateEffectiveTaxRate = (tax, income) => {
    if (income === 0) return 0;
    return (tax / income) * 100;
  };
  
  return (
    <div>
      <div className="flex items-center mb-6">
      <button
          type="button"
        onClick={onBack}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
          <ArrowLeftIcon className="w-5 h-5 mr-1" />
          Back
      </button>
        <h2 className="ml-4 text-2xl font-semibold text-gray-900">
          {isEditing ? `Edit ${getIncomeTypeLabel(incomeType)} Income` : `Add ${getIncomeTypeLabel(incomeType)} Income`}
        </h2>
      </div>
      
      {/* File upload section for Contract income */}
      {(incomeType === 'CONTRACT' || incomeType === 'CONSULTING') && (
        <div className="mb-6">
          {!uploadedFile ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center">
              <DocumentArrowUpIcon className="h-10 w-10 text-gray-400 mb-3" />
              <p className="text-sm text-gray-500 mb-4 text-center">
                Upload your contract document and we'll try to extract the key information
              </p>
              <label className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer">
                <span>Select Contract</span>
                <input 
                  type="file" 
                  accept=".pdf,.docx,.doc" 
                  className="sr-only"
                  onChange={handleFileUpload}
                />
              </label>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <DocumentArrowUpIcon className={`h-6 w-6 ${getTypeColor()}`} />
                </div>
                <div className="ml-3 flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">{uploadedFile.name}</p>
                    <button
                      onClick={() => {
                        setUploadedFile(null);
                        setProcessingError(null);
                      }}
                      className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
                    >
                      <XCircleIcon className="h-4 w-4 mr-1" /> Remove
                    </button>
                  </div>
                  
                  {/* Processing state */}
                  {isProcessing ? (
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing document...
                    </div>
                  ) : processingError ? (
                    <div className="mt-2 flex items-center text-sm text-red-600">
                      <XCircleIcon className="h-4 w-4 mr-1" /> {processingError}
                    </div>
                  ) : (
                    <div className="mt-2 flex items-center text-sm text-green-600">
                      <CheckCircleIcon className="h-4 w-4 mr-1" /> Document uploaded. You can complete the form below.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-4 flex items-start bg-blue-50 p-3 rounded-lg">
            <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 ml-2">
              If you have a contract document, we can try to extract information from it.
              You can also enter the details manually below.
            </p>
          </div>
        </div>
      )}
      
      {/* Main Form with light backgrounds */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Bucket and Owner Selector - Only show when not editing or when user has partner */}
        {!isEditing && (
          <BucketOwnerSelector
            selectedBucketId={selectedBucketId}
            selectedOwnerId={selectedOwnerId}
            onBucketChange={setSelectedBucketId}
            onOwnerChange={setSelectedOwnerId}
            disabled={isSubmitting}
          />
        )}
        
        {/* Basic Information Section */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Income Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Income Name*
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                placeholder={getIncomeTypeLabel(incomeType)}
                {...register('name', { required: 'Income name is required' })}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <XCircleIcon className="h-4 w-4 mr-1" /> {errors.name.message}
                </p>
              )}
            </div>
            
            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date*
              </label>
              <div className="relative">
                <input
                  type="date"
                  className="w-full pl-3 pr-10 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  {...register('startDate', { 
                    required: 'Start date is required',
                    validate: {
                      notInFuture: value => {
                        const date = new Date(value);
                        const now = new Date();
                        const tooFarInFuture = new Date();
                        tooFarInFuture.setFullYear(now.getFullYear() + 5); // Max 5 years in future
                        const tooFarInPast = new Date();
                        tooFarInPast.setFullYear(now.getFullYear() - 100); // Max 100 years in past
                        
                        if (date > tooFarInFuture) return 'Date cannot be more than 5 years in the future';
                        if (date < tooFarInPast) return 'Date cannot be more than 100 years in past';
                        return true;
                      }
                    }
                  })}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              {errors.startDate && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <XCircleIcon className="h-4 w-4 mr-1" /> {errors.startDate.message}
                </p>
              )}
            </div>
            
            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State*
              </label>
              <select
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                {...register('state', { required: 'State is required' })}
              >
                <option value="" disabled>Select a state</option>
                {STATE_OPTIONS.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.state && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <XCircleIcon className="h-4 w-4 mr-1" /> {errors.state.message}
                </p>
              )}
            </div>
            
            {/* Type-specific fields */}
            {/* Business Name (for Business income) */}
            {incomeType === 'BUSINESS' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  placeholder="e.g. ABC Consulting LLC"
                  {...register('businessName')}
                />
              </div>
            )}
            
            {/* Client Name (for Contract and Consulting income) */}
            {(incomeType === 'CONTRACT' || incomeType === 'CONSULTING') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  placeholder="e.g. ABC Corporation"
                  {...register('client')}
                />
              </div>
            )}
            
            {/* Property Address (for Rental income) */}
            {incomeType === 'RENTAL' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Address
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  placeholder="e.g. 123 Main St, City, State"
                  {...register('propertyAddress')}
                />
              </div>
            )}
          </div>
        </div>
        
        {/* Payment Details Section */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Payment Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount*
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 z-10">$</span>
                <input
                  type="text"
                  className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  placeholder="0.00"
                  {...register('amount', { 
                    required: 'Amount is required',
                    pattern: {
                      value: /^[0-9]*\.?[0-9]*$/,
                      message: 'Please enter a valid amount'
                    }
                  })}
                  onBlur={handleAmountBlur}
                />
              </div>
              {errors.amount && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <XCircleIcon className="h-4 w-4 mr-1" /> {errors.amount.message}
                </p>
              )}
            </div>
            
            {/* Frequency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency*
              </label>
              <select
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                {...register('frequency', { required: 'Frequency is required' })}
              >
                <option value="" disabled>Select a frequency</option>
                {FREQUENCY_OPTIONS.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.frequency && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <XCircleIcon className="h-4 w-4 mr-1" /> {errors.frequency.message}
                </p>
              )}
            </div>
            
            {/* Type-specific fields */}
            {/* Business Structure (for Business income) */}
            {incomeType === 'BUSINESS' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Structure
                </label>
                <select
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  {...register('businessStructure')}
                >
                  <option value="" disabled>Select a business structure</option>
                  {BUSINESS_STRUCTURES.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Property Type (for Rental income) */}
            {incomeType === 'RENTAL' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Type
                </label>
                <select
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  {...register('propertyType')}
                >
                  <option value="" disabled>Select a property type</option>
                  {PROPERTY_TYPES.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Payment Terms (for Contract and Consulting income) */}
            {(incomeType === 'CONTRACT' || incomeType === 'CONSULTING') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Terms
                </label>
                <select
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  {...register('paymentTerms')}
                >
                  <option value="" disabled>Select payment terms</option>
                  {PAYMENT_TERMS.map(option => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
        
        {/* Notes Section */}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Notes (Optional)</h3>
          <textarea
            className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
            rows="3"
            placeholder="Add any additional details about this income"
            {...register('notes')}
          ></textarea>
        </div>
        
        {/* Tax Preview - cleaner and more organized */}
        {watchedAmount && parseFloat(watchedAmount) > 0 && watchedFrequency && (
          <div className={`border-l-4 ${getTypeColor()} bg-blue-50 p-4 rounded-r-lg`}>
            <h4 className="text-sm font-semibold text-blue-800 mb-3">Estimated Tax Breakdown</h4>
            
            {/* Desktop view - Clean and organized table layout */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-blue-200">
                    <th className="text-left py-1 font-medium text-blue-800">Tax Type</th>
                    <th className="text-right py-1 font-medium text-blue-800">Per {formattedTaxSummary.periodLabel}</th>
                    <th className="text-right py-1 font-medium text-blue-800">Annual</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-blue-100">
                    <td className="py-2">
                      <div className="text-blue-700">Federal Tax</div>
                      <div className="text-blue-600 text-xs">
                        {calculateEffectiveTaxRate(previewTaxSummary.federal, parseFloat(watchedAmount) * 12)}% effective rate
                      </div>
                    </td>
                    <td className="py-2 text-blue-800 font-medium text-right">{formattedTaxSummary.periodFederal}</td>
                    <td className="py-2 text-blue-800 font-medium text-right">{formattedTaxSummary.federal}</td>
                  </tr>
                  <tr className="border-b border-blue-100">
                    <td className="py-2">
                      <div className="text-blue-700">State Tax</div>
                      <div className="text-blue-600 text-xs">
                        {calculateEffectiveTaxRate(previewTaxSummary.state, parseFloat(watchedAmount) * 12)}% effective rate
                      </div>
                    </td>
                    <td className="py-2 text-blue-800 font-medium text-right">{formattedTaxSummary.periodState}</td>
                    <td className="py-2 text-blue-800 font-medium text-right">{formattedTaxSummary.state}</td>
                  </tr>
                  
                  {/* Self-Employment Tax for Contractor/Business */}
                  {(incomeType === 'CONTRACTOR' || incomeType === 'BUSINESS' || incomeType === 'CONSULTING') && (
                    <tr className="border-b border-blue-100">
                      <td className="py-2">
                        <div className="text-blue-700">Self-Employment Tax</div>
                        <div className="text-blue-600 text-xs">15.3% of income (fixed rate)</div>
                      </td>
                      <td className="py-2 text-blue-800 font-medium text-right">{formattedTaxSummary.periodSelfEmployment}</td>
                      <td className="py-2 text-blue-800 font-medium text-right">{formattedTaxSummary.selfEmployment}</td>
                    </tr>
                  )}
                  
                  <tr className="border-t border-blue-300 font-medium">
                    <td className="py-2">
                      <div className="text-blue-800">Total Estimated Tax</div>
                      <div className="text-blue-600 text-xs">
                        Effective rate: {formattedTaxSummary.effectiveRate} of gross income
                      </div>
                    </td>
                    <td className="py-2 text-blue-800 font-medium text-right">{formattedTaxSummary.periodTotal}</td>
                    <td className="py-2 text-blue-800 font-medium text-right">{formattedTaxSummary.total}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            {/* Mobile view layout */}
            <div className="md:hidden space-y-4">
              <div>
                <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                  Per {formattedTaxSummary.periodLabel}:
                </h5>
                <table className="w-full text-sm">
                  <tbody>
                    <tr>
                      <td className="py-1 text-blue-700">Federal Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">{formattedTaxSummary.periodFederal}</td>
                    </tr>
                    <tr>
                      <td className="py-1 text-blue-700">State Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">{formattedTaxSummary.periodState}</td>
                    </tr>
                    {(incomeType === 'CONTRACTOR' || incomeType === 'BUSINESS' || incomeType === 'CONSULTING') && (
                      <tr>
                        <td className="py-1 text-blue-700">Self-Employment Tax:</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{formattedTaxSummary.periodSelfEmployment}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div>
                <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                  Annual:
                </h5>
                <table className="w-full text-sm">
                  <tbody>
                    <tr>
                      <td className="py-1 text-blue-700">Federal Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">{formattedTaxSummary.federal}</td>
                    </tr>
                    <tr>
                      <td className="py-1 text-blue-700">State Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">{formattedTaxSummary.state}</td>
                    </tr>
                    {(incomeType === 'CONTRACTOR' || incomeType === 'BUSINESS' || incomeType === 'CONSULTING') && (
                      <tr>
                        <td className="py-1 text-blue-700">Self-Employment Tax:</td>
                        <td className="py-1 text-blue-800 font-medium text-right">{formattedTaxSummary.selfEmployment}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              <div>
                <h5 className="font-medium text-blue-800 border-b border-blue-200 pb-1 mb-2">
                  Summary:
                </h5>
                <table className="w-full text-sm">
                  <tbody>
                    <tr>
                      <td className="py-1 text-blue-800 font-medium">Total Estimated Tax:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">{formattedTaxSummary.periodTotal} / {formattedTaxSummary.total}</td>
                    </tr>
                    <tr>
                      <td className="py-1 text-blue-700">Effective Tax Rate:</td>
                      <td className="py-1 text-blue-800 font-medium text-right">{formattedTaxSummary.effectiveRate}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <p className="text-xs text-blue-600 mt-3">
              This is an estimate based on the information provided and standard deductions. Your actual taxes may vary.
            </p>
          </div>
        )}
        
        {/* Submission Error */}
        {submissionError && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-5 w-5 text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{submissionError}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Submit button */}
        <div className="mt-8 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md flex items-center ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
          >
            {isSubmitting && (
              <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {isSubmitting 
              ? `${isEditing ? 'Updating' : 'Adding'} Income...` 
              : `${isEditing ? 'Update' : 'Add'} Income`
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default BasicIncomeForm; 